import type { GridApi, ICellRendererParams, IHeaderParams } from "ag-grid-community";
import { observer } from "mobx-react-lite";
import { useRef } from "react";

import type { GridSelectionHelpers } from "./createGridSelectionHelpers";
import type { GridSelectionStore } from "./grid-selection-store";

import Checkbox from "@/components/ui/checkbox";

interface CreateGridSelectionCellsParams<TId extends string | number, TData> {
  useSelectionStore: () => GridSelectionStore<TId>;
  getRowId: (data: TData | undefined) => TId | null;
  helpers: GridSelectionHelpers<TId, TData>;
}

export const createGridSelectionCells = <TId extends string | number, TData>({
  useSelectionStore,
  getRowId,
  helpers,
}: CreateGridSelectionCellsParams<TId, TData>) => {
  const { collectCurrentPageRowIds, collectLoadedRowIds, collectLoadedSelectedRowIds } = helpers;

  type SelectionCheckboxCellProps = ICellRendererParams<TData> & { selectionLimit?: 1 };
  type SelectionHeaderProps = IHeaderParams<TData> & { selectionLimit?: 1 };

  const SelectionCheckboxCell = observer(
    ({ data, api, node, selectionLimit }: SelectionCheckboxCellProps) => {
      const selection = useSelectionStore();
      const shiftKeyRef = useRef(false);
      const rowId = getRowId(data);
      if (rowId === null) {
        return null;
      }

      const isAllMatching = selection.mode === "all_matching";
      const checked = isAllMatching
        ? node.rowIndex !== null && node.rowIndex < (selection.matchingLimit ?? selection.count)
        : selection.has(rowId);

      const handleChange = () => {
        const wasShift = shiftKeyRef.current;
        shiftKeyRef.current = false;
        if (selectionLimit === 1) {
          if (checked) {
            selection.clear();
          } else {
            selection.replaceSelection([rowId]);
            selection.setAnchor(rowId);
          }
          return;
        }
        if (isAllMatching) {
          selection.replaceSelection(
            collectLoadedSelectedRowIds(api as GridApi<TData>, selection.count).filter(
              id => id !== rowId,
            ),
          );
          selection.setAnchor(rowId);
          return;
        }
        if (wasShift && selection.anchorId !== null && selection.anchorId !== rowId) {
          const orderedIds = collectLoadedRowIds(api as GridApi<TData>);
          const anchorIdx = orderedIds.indexOf(selection.anchorId);
          const currentIdx = orderedIds.indexOf(rowId);
          if (anchorIdx !== -1 && currentIdx !== -1) {
            const [start, end] =
              anchorIdx < currentIdx ? [anchorIdx, currentIdx] : [currentIdx, anchorIdx];
            selection.selectMany(orderedIds.slice(start, end + 1));
            return;
          }
        }
        selection.toggle(rowId);
        selection.setAnchor(rowId);
      };

      return (
        <div
          className="flex h-full w-full select-none items-center justify-center"
          onClick={e => e.stopPropagation()}
          onMouseDown={e => {
            shiftKeyRef.current = e.shiftKey;
            if (e.shiftKey) {
              e.preventDefault();
            }
          }}
        >
          <Checkbox checked={checked} onChange={handleChange} />
        </div>
      );
    },
  );

  const SelectionHeader = observer(({ api, selectionLimit }: SelectionHeaderProps) => {
    const selection = useSelectionStore();
    const isAllMatching = selection.mode === "all_matching";
    const currentPageIds = collectCurrentPageRowIds(api as GridApi<TData>);
    const currentPageSelectedCount = currentPageIds.filter(id => selection.has(id)).length;
    const matchingTotal = selection.matchingTotal ?? api.paginationGetRowCount();
    const matchingLimit = selection.matchingLimit ?? matchingTotal;
    const checked = isAllMatching
      ? matchingTotal > 0 && matchingLimit >= matchingTotal
      : currentPageIds.length > 0 && currentPageSelectedCount === currentPageIds.length;
    const isIndeterminate =
      !checked &&
      (isAllMatching
        ? matchingLimit > 0 && matchingLimit < matchingTotal
        : currentPageSelectedCount > 0 || selection.selectedCount > 0);

    return (
      <div
        className="flex h-full w-full items-center justify-center"
        onClick={e => e.stopPropagation()}
      >
        <Checkbox
          checked={selectionLimit === 1 ? false : checked}
          disabled={selectionLimit === 1}
          indeterminate={selectionLimit === 1 ? false : isIndeterminate}
          onChange={() => {
            if (checked) {
              selection.clear();
              return;
            }
            selection.setMatchingTotal(api.paginationGetRowCount());
            selection.replaceSelection(collectCurrentPageRowIds(api as GridApi<TData>));
          }}
        />
      </div>
    );
  });

  return {
    SelectionCheckboxCellRenderer: (params: SelectionCheckboxCellProps) => (
      <SelectionCheckboxCell {...params} />
    ),
    SelectionHeaderRenderer: (params: SelectionHeaderProps) => <SelectionHeader {...params} />,
  };
};
